import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, UserPlus, UserRoundX } from "lucide-react";
import Swal from "sweetalert2";
import MenuLateral from "../../Componentes/MenuLateral";
import MenuSuperior from "../../Componentes/MenuSuperior";
import { useOperador } from "../../Hooks/useOperador";
import { operadorEhAdmin } from "../../Utils/cargos";
import { assinaturaDigitalService } from "../../Services/assinaturaDigital";
import "../AssinaturaDigitalCertificado/styles.css";
import "./styles.css";

const normalizarBusca = (valor = "") => String(valor)
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .trim()
  .toLowerCase();

const AssinaturaDigitalControleAcesso = () => {
  const navigate = useNavigate();
  const { IDOperador, nome, operador } = useOperador();
  const [operadores, setOperadores] = useState([]);
  const [buscaCandidato, setBuscaCandidato] = useState("");
  const [buscaAutorizado, setBuscaAutorizado] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [alterando, setAlterando] = useState(false);
  const isAdmin = operadorEhAdmin(operador);

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const dados = await assinaturaDigitalService.listarControleAcesso();
      setOperadores(Array.isArray(dados) ? dados : []);
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.errors?.default ||
        "Nao foi possivel carregar o controle de acesso.", "error");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) { navigate("/Menu"); return; }
    carregar();
  }, [isAdmin, navigate, carregar]);

  const candidatos = useMemo(() => operadores.filter((item) =>
    item.situacao === 1 && !item.administrador && !item.autorizado), [operadores]);
  const candidatosFiltrados = useMemo(() => {
    const termo = normalizarBusca(buscaCandidato);
    if (!termo) return [];
    return candidatos.filter((item) => normalizarBusca(item.nome).includes(termo)).slice(0, 20);
  }, [candidatos, buscaCandidato]);
  const autorizados = useMemo(() => operadores.filter((item) =>
    item.autorizado && !item.administrador &&
    normalizarBusca(`${item.nome} ${item.email} ${item.IDOperador}`).includes(normalizarBusca(buscaAutorizado))),
  [operadores, buscaAutorizado]);

  const alterar = async (id, autorizado) => {
    if (!id || alterando) return;
    if (!autorizado) {
      const confirmacao = await Swal.fire({
        title: "Retirar acesso a Fila?",
        text: "O operador perdera imediatamente a permissao de visualizar e assinar documentos.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Retirar acesso",
        cancelButtonText: "Cancelar",
      });
      if (!confirmacao.isConfirmed) return;
    }
    try {
      setAlterando(true);
      await assinaturaDigitalService.definirAcessoFila(Number(id), autorizado);
      if (autorizado) setBuscaCandidato("");
      await carregar();
      Swal.fire("Sucesso", autorizado ? "Acesso concedido." : "Acesso retirado.", "success");
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.errors?.default ||
        "Nao foi possivel atualizar o acesso.", "error");
    } finally {
      setAlterando(false);
    }
  };

  return (
    <div className="menu-container-menu certificado-page">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <div className="page-shell certificado-shell">
          <MenuSuperior userId={String(IDOperador).padStart(6, "0")} userName={nome} />
          <section className="certificado-header">
            <div>
              <span><KeyRound size={17} /> Assinatura Digital</span>
              <h1>Controle de Acesso</h1>
            </div>
            <button type="button" onClick={() => navigate("/AssinaturaDigital/Certificado")}>Voltar aos certificados</button>
          </section>
          <section className="controle-acesso-card">
            <h2>Autorizar operador</h2>
            <p>A autorizacao vale para toda a Fila de Assinatura e pode ser concedida a operadores e gerentes. Apenas o ADM pode concede-la ou retira-la.</p>
            <div className="controle-acesso-busca">
              <label htmlFor="busca-operador-assinatura">Buscar operador por nome</label>
              <input id="busca-operador-assinatura" type="search" autoComplete="off"
                placeholder="Digite o nome do operador" value={buscaCandidato}
                onChange={(event) => setBuscaCandidato(event.target.value)}
                disabled={carregando || alterando} />
              {buscaCandidato.trim() && !carregando && candidatosFiltrados.length === 0 && (
                <p className="controle-acesso-vazio">Nenhum operador ativo e sem acesso foi encontrado.</p>
              )}
              {candidatosFiltrados.length > 0 && (
                <div className="controle-acesso-resultados" aria-label="Resultados da busca por operador">
                  {candidatosFiltrados.map((item) => (
                    <div className="controle-acesso-resultado" key={item.IDOperador}>
                      <div>
                        <strong>{item.nome} (#{item.IDOperador})</strong>
                        <small>{item.email || "Sem e-mail"}{item.gerente ? " - Gerente" : ""}</small>
                      </div>
                      <button type="button" onClick={() => alterar(item.IDOperador, true)}
                        disabled={alterando || carregando}>
                        <UserPlus size={17} /> Adicionar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
          <section className="controle-acesso-card">
            <div className="controle-acesso-heading">
              <div>
                <h2>Operadores autorizados</h2>
                <p>Administradores ja possuem acesso pelo cargo e nao aparecem nesta lista.</p>
              </div>
              <input aria-label="Buscar operador autorizado" placeholder="Buscar operador" value={buscaAutorizado}
                onChange={(event) => setBuscaAutorizado(event.target.value)} />
            </div>
            {carregando && <p>Carregando operadores...</p>}
            {!carregando && autorizados.length === 0 && <p>Nenhum operador autorizado encontrado.</p>}
            {!carregando && autorizados.map((item) => (
              <div className="controle-acesso-linha" key={item.IDOperador}>
                <div>
                  <strong>{item.nome} (#{item.IDOperador})</strong>
                  <small>{item.email || "Sem e-mail"}{item.situacao !== 1 ? " - Operador inativo" : ""}{item.gerente ? " - Gerente" : ""}</small>
                </div>
                <button type="button" onClick={() => alterar(item.IDOperador, false)} disabled={alterando}>
                  <UserRoundX size={17} /> Retirar acesso
                </button>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AssinaturaDigitalControleAcesso;
